import {
    PROTECTED_ELEMENTS,
    RESERVED_NAME_KEYWORDS,
    UNIQUE_ELEMENTS,
    VALID_C_VAR_NAME
} from '../../../services/constants';
import { getXmlParseErrorElement } from '../../../services/xmlParseError';
import { CoreTextModel, XMLParseCallback, stripXmlCommentsPreserveLines, validateProperty } from './common';
import { validateAssetMemoryXml } from './AssetMemoryValidationHelpers';
import { SCHEMA_ELEMENTS_TO_CHECK_FOR_REQUIRED_ATTRIBUTES } from './LVGLSchema';
import { LVGLSchemaService, ElementSchema, PropertySchema } from './LVGLSchemaService';
import {
    canCoerceToGiveType,
    collectDocumentApiProps,
    collectDocumentConsts,
    getCompatibleSubjectSuggestions,
    propHasDirectParamChild,
    PrimitiveType,
    validatePropOrConstReference,
    validatePropertyValue,
    ValidationResult,
    isValidScreenName,
    resolveScreenIdentifier,
    getDuplicateChildrenAllowTokenRanges,
    isAllowedChildElement,
    resolveAllowedChildrenParentName,
    validateElementOrder,
    type OrderValidationResult,
    type PropInfo
} from './validationHelpers';
import { normalizePath } from '../../../utils/path';
import {
    IMAGE_CONVERT_COLOR_FORMATS,
    normalizeProjectRelativeConvertPath,
    validateImageConvertPaths,
    type ImageConvertPathValidationIssue
} from '../../../code_export/generators/image_preprocessing';
export type CoreMarkerSeverity = 'error';

// Modified from Monaco API
export type CoreMarker = {
    severity: CoreMarkerSeverity;
    message: string;
    // These are all 1-based
    startLineNumber: number;
    startColumn: number;
    endLineNumber: number;
    endColumn: number;
};

type NameDefinitionRule = { element: string; parent: string | null; attribute?: string; source?: string };

type BaseDefinitionInfo = {
    source: string;
    line: number;
    startColumn: number;
    endColumn: number;
};

type GlobalDefinitionInfo = BaseDefinitionInfo & {
    file: string;
};

type DefinitionInfo = BaseDefinitionInfo & {
    parent?: string | null;
};

/**
 * The set of responsive `if_target` targets a definition applies to. `all` means the
 * definition has no `if_target` filter and therefore applies to every target.
 */
type TargetScope = { all: true } | { tokens: ReadonlySet<string> };

type LocalDefinitionInfo = DefinitionInfo & { scope: TargetScope };

type ParentStackEntry = {
    name: string;
    extends?: string;
    /** Value of the block's `if_target` attribute (used to scope name uniqueness across variants). */
    ifTarget?: string;
    /** Highest child-order group index seen so far among this element's direct children. */
    maxOrderGroupIndex?: number;
};

/** Parse an `if_target` attribute value into the target scope it selects. */
const parseTargetScope = (ifTarget?: string): TargetScope => {
    const trimmed: string = ifTarget?.trim() ?? '';
    if (!trimmed) return { all: true };

    const tokens: Set<string> = new Set(
        trimmed
            .split('|')
            .map((token: string) => token.trim())
            .filter(Boolean)
    );
    return tokens.size ? { tokens } : { all: true };
};

/**
 * Whether two definitions can apply to the same target. Definitions with different, explicit
 * `if_target` variants never collide; a definition without a filter ('all') overlaps every scope.
 */
const targetScopesOverlap = (a: TargetScope, b: TargetScope): boolean => {
    if ('all' in a || 'all' in b) return true;
    for (const token of a.tokens) if (b.tokens.has(token)) return true;
    return false;
};

/** Schema element types that represent instantiable, named UI elements inside `<view>`. */
const WIDGET_INSTANCE_ELEMENT_TYPES: ReadonlySet<string> = new Set<string>(['lvgl-widget', 'widget', 'component']);

let foundSubjectForValueValidation: [string, Set<string>] | undefined;
const ROOT_NAME_BY_FILENAME: ReadonlySet<string> = new Set<string>(['component', 'widget', 'screen']);
const CONST_ELEMENTS: ReadonlySet<string> = new Set<string>([
    'int',
    'point',
    'color',
    'px',
    'percentage',
    'string',
    'opa',
    'bool'
]);
const NAME_BY_PARENT_RULES: NameDefinitionRule[] = [
    { element: 'event_cb', parent: null, attribute: 'callback' },
    { element: 'lv_obj-event_cb', parent: null, attribute: 'callback' },
    { element: 'bin', parent: 'fonts' },
    { element: 'tiny_ttf', parent: 'fonts' },
    { element: 'data', parent: 'images' },
    { element: 'file', parent: 'images' },
    { element: 'int', parent: 'subjects', source: 'subject' },
    { element: 'float', parent: 'subjects', source: 'subject' },
    { element: 'string', parent: 'subjects', source: 'subject' },
    { element: 'pointer', parent: 'subjects', source: 'subject' },
    { element: 'style', parent: 'styles' },
    // Const definitions. May legitimately repeat across `if_target` variant <consts> blocks;
    // uniqueness is enforced per target scope in registerDefinition.
    ...Array.from(CONST_ELEMENTS, (element: string): NameDefinitionRule => ({
        element,
        parent: 'consts',
        source: 'const'
    })),
    // Public API property names, unique within a component/widget definition.
    { element: 'prop', parent: 'api', source: 'prop' },
    // Animation timeline names, unique within <animations>.
    { element: 'timeline', parent: 'animations', source: 'timeline' }
];
const GLOBAL_NAME_DEFINITIONS: Map<string, GlobalDefinitionInfo> = new Map();
const GLOBAL_DUPLICATE_DEFINITIONS: Map<string, GlobalDefinitionInfo[]> = new Map();
/** Event callback references keyed by callback name (one entry per file that uses the name). */
const EVENT_CALLBACK_DEFINITIONS: Map<string, GlobalDefinitionInfo[]> = new Map();
const POSITIVE_INTEGER_PATTERN: RegExp = /^[1-9]\d*$/;
const POSITIVE_PERCENT_PATTERN: RegExp = /^(\d+(?:\.\d+)?)%$/;
const IMAGE_CONVERT_COLOR_FORMAT_SET: ReadonlySet<string> = new Set<string>(IMAGE_CONVERT_COLOR_FORMATS);

const clearDefinitionsForFile = (
    filePath: string,
    shouldClear: (info: GlobalDefinitionInfo) => boolean = () => true
): void => {
    if (!filePath) return;

    const normalizedPath: string = normalizePath(filePath);

    Array.from(GLOBAL_NAME_DEFINITIONS.entries()).forEach(([name, info]) => {
        if (info.file === normalizedPath && shouldClear(info)) GLOBAL_NAME_DEFINITIONS.delete(name);
    });

    Array.from(GLOBAL_DUPLICATE_DEFINITIONS.entries()).forEach(([name, arr]) => {
        const filtered: GlobalDefinitionInfo[] = arr.filter(
            (info: GlobalDefinitionInfo) => !(info.file === normalizedPath && shouldClear(info))
        );
        if (filtered.length === 0) GLOBAL_DUPLICATE_DEFINITIONS.delete(name);
        else GLOBAL_DUPLICATE_DEFINITIONS.set(name, filtered);
    });

    Array.from(EVENT_CALLBACK_DEFINITIONS.entries()).forEach(([name, arr]) => {
        const filtered: GlobalDefinitionInfo[] = arr.filter(
            (info: GlobalDefinitionInfo) => !(info.file === normalizedPath && shouldClear(info))
        );
        if (filtered.length === 0) EVENT_CALLBACK_DEFINITIONS.delete(name);
        else EVENT_CALLBACK_DEFINITIONS.set(name, filtered);
    });
};

const isEventCallbackElement = (elementName: string): boolean =>
    elementName === 'event_cb' || elementName === 'lv_obj-event_cb';

const isGlobalNameDefinition = (elementName: string, parent: string | null): boolean => {
    // Root component/widget names are registered globally at their definition site (see the
    // filename-derived registration in XMLValidatorCore); this handles the remaining globally
    // scoped names (screens, fonts, images, subjects).
    if (elementName === 'component' || elementName === 'screen') return true;
    if ((elementName === 'bin' || elementName === 'tiny_ttf') && parent === 'fonts') return true;
    if ((elementName === 'data' || elementName === 'file') && parent === 'images') return true;
    if (parent === 'subjects') return true;
    return false;
};

const findPropInfo = (props: readonly PropInfo[], propName: string): PropInfo | undefined =>
    props.find((prop: PropInfo) => prop.propName === propName);

const getPropValueRange = (
    prop: PropInfo
): Pick<CoreMarker, 'startLineNumber' | 'startColumn' | 'endLineNumber' | 'endColumn'> => {
    const assignmentOffset: number = prop.fullProp.indexOf('=');
    const valueQuoteOffset: number =
        assignmentOffset >= 0 ? prop.fullProp.slice(assignmentOffset + 1).search(/["']/) : -1;
    const valueStartOffset: number =
        assignmentOffset >= 0 && valueQuoteOffset >= 0
            ? assignmentOffset + valueQuoteOffset + 2
            : prop.propName.length + 2;
    const startColumn: number = prop.startColumn + valueStartOffset;

    return {
        startLineNumber: prop.lineNumber,
        startColumn,
        endLineNumber: prop.lineNumber,
        endColumn: startColumn + prop.propValue.length
    };
};

const pushPropValueMarker = (markers: CoreMarker[], prop: PropInfo, message: string): void => {
    markers.push({
        severity: 'error',
        message,
        ...getPropValueRange(prop)
    });
};

const getDocumentConstValue = (parsedDocument: Document, constName: string): string | undefined => {
    const constElements: Element[] = Array.from(parsedDocument.querySelectorAll('consts > *'));
    const matchingConst: Element | undefined = constElements.find(
        (element: Element) => element.getAttribute('name') === constName
    );
    return matchingConst?.getAttribute('value') ?? undefined;
};

const resolveSameDocumentConstValue = (
    parsedDocument: Document,
    prop: PropInfo,
    markers: CoreMarker[]
): string | undefined => {
    const trimmed: string = prop.propValue.trim();
    if (!trimmed.startsWith('#')) return trimmed;

    const constName: string = trimmed.slice(1).trim();
    if (!constName) {
        pushPropValueMarker(markers, prop, `<convert> '${prop.propName}' references an empty #const name.`);
        return undefined;
    }

    const constValue: string | undefined = getDocumentConstValue(parsedDocument, constName);
    if (constValue === undefined) {
        pushPropValueMarker(
            markers,
            prop,
            `<convert> '${prop.propName}' can only reference a const from the same globals.xml file. Missing '#${constName}'.`
        );
        return undefined;
    }

    return constValue.trim();
};

const validateImageConvertDimensionProp = (parsedDocument: Document, prop: PropInfo, markers: CoreMarker[]): void => {
    const resolvedValue: string | undefined = resolveSameDocumentConstValue(parsedDocument, prop, markers);
    if (resolvedValue === undefined) return;

    if ((prop.propName === 'width' || prop.propName === 'height') && !POSITIVE_INTEGER_PATTERN.test(resolvedValue)) {
        pushPropValueMarker(
            markers,
            prop,
            `<convert> '${prop.propName}' must be a positive integer or a #const with a positive integer value.`
        );
        return;
    }

    if (prop.propName === 'scale') {
        const percentMatch: RegExpExecArray | null = POSITIVE_PERCENT_PATTERN.exec(resolvedValue);
        const percentValue: number = percentMatch ? Number(percentMatch[1]) : 0;
        if (!percentMatch || !Number.isFinite(percentValue) || percentValue <= 0) {
            pushPropValueMarker(
                markers,
                prop,
                `<convert> 'scale' must be a positive percent value like '50%' or a #const with that value.`
            );
        }
    }
};

const validateImageConvertElement = (
    parsedDocument: Document,
    props: readonly PropInfo[],
    convertDestinations: Map<string, DefinitionInfo>,
    markers: CoreMarker[]
): void => {
    const srcProp: PropInfo | undefined = findPropInfo(props, 'src');
    const destProp: PropInfo | undefined = findPropInfo(props, 'dest');
    const widthProp: PropInfo | undefined = findPropInfo(props, 'width');
    const heightProp: PropInfo | undefined = findPropInfo(props, 'height');
    const scaleProp: PropInfo | undefined = findPropInfo(props, 'scale');
    const colorFormatProp: PropInfo | undefined = findPropInfo(props, 'color_format');

    if (srcProp && destProp) {
        const issues: ImageConvertPathValidationIssue[] = validateImageConvertPaths({
            src: srcProp.propValue,
            dest: destProp.propValue
        });

        for (const issue of issues) {
            const prop: PropInfo = issue.attribute === 'src' ? srcProp : destProp;
            pushPropValueMarker(markers, prop, issue.message);
        }

        const normalizedDest: string = normalizeProjectRelativeConvertPath(destProp.propValue);
        if (normalizedDest) {
            const existingDestination: DefinitionInfo | undefined = convertDestinations.get(normalizedDest);
            if (existingDestination) {
                pushPropValueMarker(
                    markers,
                    destProp,
                    `<convert> 'dest' collides with another conversion destination declared on line ${existingDestination.line}.`
                );
            } else {
                convertDestinations.set(normalizedDest, {
                    source: normalizedDest,
                    line: destProp.lineNumber,
                    startColumn: destProp.startColumn,
                    endColumn: destProp.endColumn,
                    parent: 'images'
                });
            }
        }
    }

    if (scaleProp && (widthProp || heightProp)) {
        pushPropValueMarker(markers, scaleProp, `<convert> 'scale' cannot be combined with 'width' or 'height'.`);
    }

    [widthProp, heightProp, scaleProp].forEach((prop: PropInfo | undefined): void => {
        if (prop) validateImageConvertDimensionProp(parsedDocument, prop, markers);
    });

    if (colorFormatProp) {
        const normalizedColorFormat: string = colorFormatProp.propValue.trim().toLowerCase();
        if (!IMAGE_CONVERT_COLOR_FORMAT_SET.has(normalizedColorFormat)) {
            pushPropValueMarker(
                markers,
                colorFormatProp,
                `<convert> color_format '${colorFormatProp.propValue}' is not supported. Supported: ${IMAGE_CONVERT_COLOR_FORMATS.join(', ')}.`
            );
        }
    }
};

export const XMLValidatorCore = (
    model: CoreTextModel | null | undefined,
    schemaService: LVGLSchemaService | null,
    parseXml: XMLParseCallback
): CoreMarker[] => {
    if (!model || !schemaService) return [];

    const markers: CoreMarker[] = [];
    const text: string = model.getValue();
    const parentStack: ParentStackEntry[] = [];
    const filePath: string = model.uri?.path ?? '';
    const normalizedFilePath: string = normalizePath(filePath);
    const fileName: string = filePath.split(/[/\\]/).pop() ?? '';
    const fileBaseName: string = fileName.replace(/\.[^/.]+$/, '');
    const localDefinitions: Map<string, LocalDefinitionInfo[]> = new Map();
    const convertDestinations: Map<string, DefinitionInfo> = new Map();
    const globalReservedNames: Set<string> = new Set([
        ...schemaService.globalStyles.keys(),
        ...schemaService.globalConsts.keys(),
        ...schemaService.enums.keys(),
        ...schemaService.subjects,
        ...schemaService.images,
        ...schemaService.fonts
    ]);

    // Drop any stale global entries for this file (e.g., after edits).
    clearDefinitionsForFile(normalizedFilePath);

    const registerDefinition = (
        name: string,
        info: DefinitionInfo,
        isGlobal: boolean,
        ifTarget?: string,
        // Whether this name participates in the file-local uniqueness check. Component/widget
        // root names (derived from the filename) live in their own LVGL namespace — a component
        // `hello.xml` is referenced as `<hello/>`, distinct from a const `#hello`, a subject
        // `$hello` or a style named `hello` — so they are registered globally only and must not
        // collide with same-file resources.
        trackLocally: boolean = true
    ): void => {
        if (!name) return;

        const scope: TargetScope = parseTargetScope(ifTarget);
        const existingLocals: LocalDefinitionInfo[] = localDefinitions.get(name) ?? [];
        // A name may repeat only across non-overlapping `if_target` variants; otherwise it collides.
        // Styles are the exception: the same style name may be declared by multiple <style> blocks
        // in one file (e.g. one block per state/part), so two styles never collide with each other —
        // only clashes with a *different* resource kind (const, subject, widget, ...) are reported.
        const conflictingLocal: LocalDefinitionInfo | undefined = trackLocally
            ? existingLocals.find(
                  (existing: LocalDefinitionInfo) =>
                      targetScopesOverlap(existing.scope, scope) &&
                      !(existing.source === 'style' && info.source === 'style')
              )
            : undefined;

        if (conflictingLocal) {
            markers.push({
                severity: 'error',
                message: `Name '${name}' is already used by another resource (${conflictingLocal.source}).`,
                startLineNumber: info.line,
                startColumn: info.startColumn,
                endLineNumber: info.line,
                endColumn: info.endColumn
            });
        }

        const existingGlobal: GlobalDefinitionInfo | undefined = isGlobal
            ? GLOBAL_NAME_DEFINITIONS.get(name)
            : undefined;
        const knownDuplicates: GlobalDefinitionInfo[] | undefined = isGlobal
            ? GLOBAL_DUPLICATE_DEFINITIONS.get(name)
            : undefined;

        const pushGlobalConflict = (conflict: GlobalDefinitionInfo): void => {
            markers.push({
                severity: 'error',
                message: `Name '${name}' is already used in file '${conflict.file}' (${conflict.source}).`,
                startLineNumber: info.line,
                startColumn: info.startColumn,
                endLineNumber: info.line,
                endColumn: info.endColumn
            });
        };

        if (isGlobal && knownDuplicates?.length) {
            const conflicts: GlobalDefinitionInfo[] = knownDuplicates.filter((d) => d.file !== normalizedFilePath);
            (conflicts.length ? conflicts : knownDuplicates).forEach(pushGlobalConflict);
        }

        if (isGlobal && existingGlobal && existingGlobal.file !== normalizedFilePath) {
            const duplicates: GlobalDefinitionInfo[] = knownDuplicates ?? [];
            duplicates.push(existingGlobal, { ...info, file: normalizedFilePath || '<unknown>' });
            GLOBAL_DUPLICATE_DEFINITIONS.set(name, duplicates);
            pushGlobalConflict(existingGlobal);
        }

        if (trackLocally) {
            existingLocals.push({ ...info, scope });
            localDefinitions.set(name, existingLocals);
        }
        if (isGlobal && (!existingGlobal || existingGlobal.file === normalizedFilePath))
            GLOBAL_NAME_DEFINITIONS.set(name, { ...info, file: normalizedFilePath || '<unknown>' });
    };

    // Remove XML comments before processing
    const textWithoutComments: string = stripXmlCommentsPreserveLines(text);

    const inGlobalsDocument: boolean = textWithoutComments.trimStart().startsWith('<globals');

    // Check for unwrapped text content - match text between tags, after closing tags, and before opening tags
    const TAG_REGEXP: RegExp = /(<\/?[\w\-_]+(?:\s+[^>]*)?\/?>)|([^<]+)/g;
    let match: RegExpExecArray | null;
    let hasWidget: boolean = textWithoutComments.includes('<widget>') && textWithoutComments.includes('</widget>');

    while ((match = TAG_REGEXP.exec(textWithoutComments)) !== null) {
        const [_, tag, text] = match;

        if (!tag && text?.trim()) {
            // This is text content - validate it is not outside tags
            const beforeMatch: string = textWithoutComments.substring(0, match.index);
            const lines: string[] = beforeMatch.split('\n');
            const startLineNumber: number = lines.length;

            // Find the actual start of the text (ignoring leading whitespace)
            const textStart: number = text.search(/\S/);
            const startColumn: number = lines[lines.length - 1].length + 1 + textStart;

            // Calculate end position, handling multiple lines
            const textLines: string[] = text.split('\n');
            const endLineNumber: number = startLineNumber + textLines.length - 1;
            const endColumn: number =
                textLines.length === 1 ? startColumn + text.trim().length : textLines[textLines.length - 1].length + 1;

            markers.push({
                severity: 'error',
                message: 'Text content must be enclosed in proper XML elements',
                startLineNumber,
                startColumn,
                endLineNumber,
                endColumn
            });
        }
    }

    // Check for XML parsing errors
    const parsedDocument: Document = parseXml(text);
    const parseError: Element | null = getXmlParseErrorElement(parsedDocument);
    if (parseError) {
        // Find the location info from the error message
        const errorText: string = parseError.textContent || '';
        const lineMatch: RegExpMatchArray | null = errorText.match(/line(?: number)? (\d+)/i);
        const lineNumber: number = lineMatch ? parseInt(lineMatch[1]) : 1;

        markers.push({
            severity: 'error',
            message: errorText,
            startLineNumber: lineNumber,
            startColumn: 1,
            endLineNumber: lineNumber,
            endColumn: model.getLineLength(lineNumber) + 1
        });
    } else {
        // Perform validation for specific structure --> asset memory
        markers.push(
            ...validateAssetMemoryXml(text, parsedDocument, schemaService).map((marker) => ({
                severity: 'error' as const,
                ...marker
            }))
        );
    }
    const documentApiPropNames: string[] = collectDocumentApiProps(parsedDocument);
    const documentConstNames: string[] = collectDocumentConsts(parsedDocument);

    // Instead of processing line by line, process the entire document at once
    const elementMatches: RegExpExecArray[] = Array.from(
        textWithoutComments.matchAll(/<\/?([a-zA-Z][\w-]*)([\s\S]*?)>/g)
    );

    for (const match of elementMatches) {
        const [fullMatch, elementName, attributes] = match;
        const matchStartIndex: number = match.index ?? text.indexOf(fullMatch);

        // Calculate the line and column information
        const beforeMatch: string = text.substring(0, matchStartIndex);
        const lines: string[] = beforeMatch.split('\n');
        const lineNumber: number = lines.length;
        const startColumn: number = lines[lines.length - 1].length + 1;
        const isClosing: boolean = fullMatch.trimStart().startsWith('</');
        const elementNameStartColumn: number = startColumn + (isClosing ? 2 : 1);
        const elementNameRange = {
            startLineNumber: lineNumber,
            startColumn: elementNameStartColumn,
            endLineNumber: lineNumber,
            endColumn: elementNameStartColumn + elementName.length
        };

        if (isClosing) {
            const expectedElement: ParentStackEntry | undefined = parentStack.pop();
            if (expectedElement && expectedElement.name !== elementName) {
                markers.push({
                    severity: 'error',
                    message: `Mismatched closing tag: expected '${expectedElement.name}', found '${elementName}'`,
                    startLineNumber: lineNumber,
                    startColumn,
                    endLineNumber: lineNumber,
                    endColumn: startColumn + fullMatch.length
                });
            }

            // Test for whitespace between the tag name and the closing '>'.
            // Example: </view >
            // This is valid XML but the LVGL parser cannot handle it
            if (/\s+>$/.test(fullMatch)) {
                markers.push({
                    severity: 'error',
                    message: `Invalid whitespace before closing bracket '>' in tag '${elementName}'`,
                    startLineNumber: lineNumber,
                    startColumn,
                    endLineNumber: lineNumber,
                    endColumn: startColumn + fullMatch.length
                });
            }
        } else {
            const parentEntry: ParentStackEntry | undefined = parentStack[parentStack.length - 1];
            const parentElement: string = parentEntry?.name || '';
            let elementExtends: string | undefined;
            // Opening element validation (both regular and self-closing tags)
            if (!schemaService.hasElement(elementName)) {
                markers.push({
                    severity: 'error',
                    message: `Unknown element: ${elementName}`,
                    ...elementNameRange
                });
            } else if (
                parentStack.length === 0 &&
                PROTECTED_ELEMENTS.has(elementName) &&
                fileName.toLowerCase() === 'test.xml'
            ) {
                markers.push({
                    severity: 'error',
                    message: `Root element '${elementName}' is not allowed when the file is named 'test.xml'.`,
                    ...elementNameRange
                });
            } else {
                // Validate attributes if element is valid
                const propMatches: RegExpExecArray[] = Array.from(attributes.matchAll(/(\b[\w-]+)=["']([^"']*)["']/g));
                const propsAndArgsToValidate: PropInfo[] = [];

                // Root name definitions derived from filename
                if (parentStack.length === 0 && ROOT_NAME_BY_FILENAME.has(elementName) && elementName !== 'screen') {
                    const rootName: string = fileBaseName.trim();
                    if (rootName) {
                        registerDefinition(
                            rootName,
                            {
                                source: elementName,
                                line: lineNumber,
                                startColumn: startColumn + 1,
                                endColumn: startColumn + elementName.length + 1,
                                parent: null
                            },
                            // Components and widgets are instantiated by the same filename-derived
                            // tag (`<hello/>`), so their names share one global namespace: two
                            // `hello.xml` files — component or widget — must collide.
                            true,
                            undefined,
                            // The component/widget name shares the filename but occupies a separate
                            // namespace from same-file resources, so it must not collide with the
                            // file's own consts, styles, etc.
                            false
                        );
                    }
                }

                // First pass: gather all props and their positions
                const elementNameOffsetInMatch: number = fullMatch.indexOf(elementName);
                const attributesOffsetInMatch: number =
                    elementNameOffsetInMatch >= 0 ? elementNameOffsetInMatch + elementName.length : 0;

                for (const propMatch of propMatches) {
                    const [fullProp, propName, propValue] = propMatch;

                    // Calculate position info once
                    const propOffsetInAttributes: number = propMatch.index ?? attributes.indexOf(fullProp);
                    const beforeProp: string = textWithoutComments.substring(
                        0,
                        matchStartIndex + attributesOffsetInMatch + propOffsetInAttributes
                    );
                    const propLines: string[] = beforeProp.split('\n');
                    const lineNumber: number = propLines.length;
                    const startColumn: number = propLines[propLines.length - 1].length + 1;

                    propsAndArgsToValidate.push({
                        fullProp,
                        propName,
                        propValue,
                        lineNumber,
                        startColumn,
                        endColumn: startColumn + fullProp.length
                    });
                }

                // Check if element extends another element
                const extendsInfo: PropInfo | undefined = propsAndArgsToValidate.find(
                    (prop) => prop.propName === 'extends'
                );
                const extendedElement: string | undefined = extendsInfo?.propValue;
                elementExtends = elementName === 'view' ? extendedElement || 'lv_obj' : undefined;

                if (elementName === 'convert' && parentElement === 'images') {
                    validateImageConvertElement(parsedDocument, propsAndArgsToValidate, convertDestinations, markers);
                }

                if (parentStack.length === 0 && elementName === 'screen') {
                    const nameProp: PropInfo | undefined = propsAndArgsToValidate.find(
                        (prop) => prop.propName === 'name'
                    );
                    const permanentProp: PropInfo | undefined = propsAndArgsToValidate.find(
                        (prop) => prop.propName === 'permanent'
                    );
                    const screenName: string = resolveScreenIdentifier(filePath, nameProp?.propValue);

                    if (nameProp && !isValidScreenName(screenName)) {
                        markers.push({
                            severity: 'error',
                            message: `Invalid screen 'name' value: '${screenName}'. Use path-like names (e.g. 'main_menu' or 'folder/screen1') with valid identifier segments.`,
                            startLineNumber: nameProp.lineNumber,
                            startColumn: nameProp.startColumn,
                            endLineNumber: nameProp.lineNumber,
                            endColumn: nameProp.endColumn
                        });
                    } else if (!nameProp && !VALID_C_VAR_NAME.test(screenName)) {
                        markers.push({
                            severity: 'error',
                            message: `Invalid screen identifier '${screenName}' derived from file name '${fileBaseName}.xml'. Rename the file to a valid identifier, or set an explicit 'name' attribute.`,
                            ...elementNameRange
                        });
                    }

                    const conflictPath: string | undefined = schemaService.findScreenNameConflict(screenName, filePath);
                    if (conflictPath && screenName) {
                        const conflictFile: string = conflictPath.split(/[/\\]/).pop() ?? conflictPath;
                        markers.push({
                            severity: 'error',
                            message: `Screen name '${screenName}' is already used by '${conflictFile}'. Choose a unique name attribute or file name.`,
                            startLineNumber: nameProp?.lineNumber ?? lineNumber,
                            startColumn: nameProp?.startColumn ?? startColumn + 1,
                            endLineNumber: nameProp?.lineNumber ?? lineNumber,
                            endColumn: nameProp?.endColumn ?? startColumn + elementName.length + 1
                        });
                    }

                    schemaService.registerScreenName(
                        filePath,
                        nameProp?.propValue,
                        permanentProp?.propValue === 'true'
                    );

                    if (screenName) {
                        registerDefinition(
                            screenName,
                            {
                                source: elementName,
                                line: nameProp?.lineNumber ?? lineNumber,
                                startColumn: nameProp?.startColumn ?? startColumn + 1,
                                endColumn: nameProp?.endColumn ?? startColumn + elementName.length + 1,
                                parent: null
                            },
                            true
                        );
                    }
                }

                // Get all valid properties including those from extended element.
                const [validOwnProperties, validInheritedProperties] = schemaService.getPropertyNames(
                    elementName,
                    extendedElement, // Pass the extended element name if it exists
                    parentElement
                );
                const allValidProperties: string[] = [...validOwnProperties, ...validInheritedProperties];

                if (elementName === 'prop') {
                    const typeProp: PropInfo | undefined = propsAndArgsToValidate.find(
                        (prop) => prop.propName === 'type'
                    );
                    const hasValidTypeAttr: boolean = (typeProp?.propValue.trim().length ?? 0) > 0;
                    const tagStartIndex: number = match.index ?? textWithoutComments.indexOf(fullMatch);
                    const hasParamChild: boolean = propHasDirectParamChild(
                        textWithoutComments,
                        tagStartIndex,
                        fullMatch
                    );

                    if (!hasValidTypeAttr && !hasParamChild) {
                        markers.push({
                            severity: 'error',
                            message: `Missing required property 'type' for element 'prop'`,
                            ...elementNameRange
                        });
                    }

                    if (hasValidTypeAttr && hasParamChild) {
                        markers.push({
                            severity: 'error',
                            message: `Element 'prop' cannot define both a 'type' attribute and 'param' children`,
                            ...elementNameRange
                        });
                    }
                }

                // Second pass: check for required attributes
                const elementSchema: ElementSchema | undefined = schemaService.getElement(elementName);
                const elementType: string | undefined = elementSchema?.type;

                if (!elementType) throw new Error(`Cannot validate '${elementName}' because it has no type`);

                // Should check naming convention of registered core XML elements
                const namePropValue: string | undefined = propsAndArgsToValidate
                    .find((p) => p.propName === 'name')
                    ?.propValue.toLocaleLowerCase()
                    .trim();
                const [, namePropertyOwner] = schemaService.getPropertyType(
                    elementName,
                    'name',
                    extendedElement,
                    parentElement
                );
                const isObjectNameProperty: boolean = namePropertyOwner === elementName;

                if (
                    namePropValue &&
                    !namePropValue.startsWith('$') &&
                    !namePropValue.startsWith('#') &&
                    (PROTECTED_ELEMENTS.has(parentElement) || PROTECTED_ELEMENTS.has(elementName)) &&
                    isObjectNameProperty &&
                    (RESERVED_NAME_KEYWORDS.has(namePropValue) || !/^[A-Za-z0-9]/.test(namePropValue))
                ) {
                    markers.push({
                        severity: 'error',
                        message: `Name '${namePropValue}' cannot be used in '${elementName}' because '${namePropValue}' is a reserved keyword.`,
                        ...elementNameRange
                    });
                }

                // Check if passing API reference is valid and supported
                if (hasWidget) {
                    propsAndArgsToValidate
                        .filter((p) => p.propValue.startsWith('$'))
                        .forEach((invalid: PropInfo) => {
                            markers.push({
                                severity: 'error',
                                message: `Referencing <api> is not supported in '${elementName}', only in <component>.`,
                                startLineNumber: invalid.lineNumber,
                                startColumn: invalid.startColumn,
                                endLineNumber: invalid.lineNumber,
                                endColumn: invalid.endColumn
                            });
                        });
                }

                // Should only check components and built-in parts of the schema
                if (
                    elementType === 'component' ||
                    SCHEMA_ELEMENTS_TO_CHECK_FOR_REQUIRED_ATTRIBUTES.includes(elementName)
                ) {
                    const propIsRequired = (prop: PropertySchema): boolean =>
                        prop.default === undefined && prop.required !== false;

                    // Get a list of all required properties
                    const requiredOwnProperties: string[] = validOwnProperties
                        .map((propName: string) => schemaService.lookupProperty(propName, elementName)?.propSchema)
                        .filter((propSchema: PropertySchema | undefined) => propSchema && propIsRequired(propSchema))
                        .filter((p) => p !== undefined)
                        .map((p) => p.name)
                        .filter(
                            (propName) => !(elementName === 'screen' && parentStack.length === 0 && propName === 'name')
                        );

                    // Check if any required properties are missing from the actual props present
                    const presentPropNames: string[] = propsAndArgsToValidate.map((p) => p.propName);
                    for (const requiredProp of requiredOwnProperties) {
                        if (!presentPropNames.includes(requiredProp)) {
                            markers.push({
                                severity: 'error',
                                message: `Missing required property '${requiredProp}' for element '${elementName}'`,
                                ...elementNameRange
                            });
                        }
                    }
                }

                // Third pass: require args
                if (elementSchema?.args) {
                    // Only require the args with no default
                    for (const arg of elementSchema?.args.filter((a) => a.default === undefined)) {
                        const hasArg: PropInfo | undefined = propsAndArgsToValidate.find(
                            (p) => p.propName === arg.name
                        );
                        if (!hasArg) {
                            markers.push({
                                severity: 'error',
                                message: `Missing required argument '${arg.name}' for element '${elementName}'`,
                                ...elementNameRange
                            });
                        }
                    }
                }

                // Fourth pass: validate all props and check for duplicated keys
                for (const prop of propsAndArgsToValidate) {
                    const replicatedNames: Set<string> = propsAndArgsToValidate.reduce<{
                        seen: Set<string>;
                        multiples: Set<string>;
                    }>(
                        (acc, { propName }) => (
                            acc.seen.has(propName) ? acc.multiples.add(propName) : acc.seen.add(propName),
                            acc
                        ),
                        { seen: new Set(), multiples: new Set() }
                    ).multiples;

                    if (replicatedNames.has(prop.propName)) {
                        markers.push({
                            severity: 'error',
                            message: `Non-unique '${prop.propName}' for element '${elementName}'`,
                            startLineNumber: prop.lineNumber,
                            startColumn: prop.startColumn,
                            endLineNumber: prop.lineNumber,
                            endColumn: prop.startColumn + prop.propName.length
                        });
                    }

                    // Could be selector syntax, in which case it'll return a cleaned name.
                    const propNameToUse: string = validateProperty(
                        schemaService,
                        prop,
                        markers,
                        allValidProperties,
                        parentElement,
                        elementName,
                        extendedElement
                    );

                    if (elementName === 'style' && prop.propName === 'name') {
                        const styleName: string = prop.propValue.trim();
                        if (PROTECTED_ELEMENTS.has(styleName)) {
                            markers.push({
                                severity: 'error',
                                message: `Style name '${styleName}' cannot match any globals (e.g. ${Array.from(
                                    PROTECTED_ELEMENTS
                                ).join(', ')}).`,
                                startLineNumber: prop.lineNumber,
                                startColumn: prop.startColumn,
                                endLineNumber: prop.lineNumber,
                                endColumn: prop.endColumn
                            });
                        }
                    }

                    // Validate property value
                    const [propType] = schemaService.getPropertyType(
                        elementName,
                        prop.propName,
                        extendedElement,
                        parentElement
                    );

                    if (
                        parentElement == 'previews' &&
                        elementName === 'preview' &&
                        (prop.propName === 'width' || prop.propName === 'height')
                    ) {
                        const refCheck: ValidationResult | undefined = validatePropOrConstReference(
                            prop.propValue.trim(),
                            schemaService,
                            documentConstNames
                        );
                        if (!refCheck && !/^-?\d+$/.test(prop.propValue.trim())) {
                            markers.push({
                                severity: 'error',
                                message: `Invalid value for '${prop.propName}': must be an integer, a #constant, or a $prop reference`,
                                ...getPropValueRange(prop)
                            });
                            continue;
                        }
                        if (refCheck) continue;
                    }

                    if (Object.entries(schemaService.subjectsByType).some(([_, set]) => set.has(prop.propValue))) {
                        foundSubjectForValueValidation = Object.entries(schemaService.subjectsByType).find(([_, set]) =>
                            set.has(prop.propValue)
                        );
                    }

                    if (
                        foundSubjectForValueValidation &&
                        propType === 'primitive' &&
                        prop.propName === 'value' &&
                        !canCoerceToGiveType(foundSubjectForValueValidation[0] as PrimitiveType, prop.propValue)
                    ) {
                        markers.push({
                            severity: 'error',
                            message: `Invalid subject '${prop.propValue}', ${propType} type value could not be coerced`,
                            startLineNumber: prop.lineNumber,
                            startColumn: prop.startColumn,
                            endLineNumber: prop.lineNumber,
                            endColumn: prop.startColumn + prop.propName.length
                        });
                        foundSubjectForValueValidation = undefined;
                    }

                    if (elementName === 'convert' && parentElement === 'images' && prop.propName === 'color_format') {
                        continue;
                    }

                    // Disallow 'if_target' in widget views - not currently supported
                    if (
                        elementName === 'view' &&
                        prop.propName === 'if_target' &&
                        parentStack.some((entry) => entry.name === 'widget')
                    ) {
                        markers.push({
                            severity: 'error',
                            message: `'if_target' is not allowed in '<view>' inside '<widget>'.`,
                            ...getPropValueRange(prop)
                        });
                        continue;
                    }

                    const validation: ValidationResult = validatePropertyValue(
                        textWithoutComments,
                        propNameToUse,
                        prop.propValue,
                        propType as string,
                        schemaService,
                        elementName,
                        parentStack.length === 0,
                        {
                            parent: parentElement,
                            propsAndArgsToValidate,
                            documentConstNames,
                            knownScreenNames: Array.from(
                                new Set([...schemaService.getScreenNames(), ...getCachedScreenNames()])
                            )
                        }
                    );
                    const isTranslationTextProperty: boolean =
                        elementName === 'translation' && propType === 'string:translation';
                    if (!validation.isValid) {
                        markers.push({
                            severity: 'error',
                            message: `Invalid value for '${propNameToUse}': ${validation.message}`,
                            ...getPropValueRange(prop)
                        });
                    }

                    // Collect name definitions according to custom rules
                    const nameDefinitionRule: NameDefinitionRule | undefined = NAME_BY_PARENT_RULES.find(
                        ({ element, parent, attribute = 'name' }) =>
                            element === elementName &&
                            prop.propName === attribute &&
                            (parent === null || parent === parentElement)
                    );
                    const registersNameDefinition: boolean =
                        (prop.propName === 'name' && isObjectNameProperty) || nameDefinitionRule !== undefined;

                    if (registersNameDefinition) {
                        const canonicalName: string = prop.propValue.trim();
                        if (canonicalName) {
                            if (
                                prop.propName === 'name' &&
                                parentElement === 'consts' &&
                                CONST_ELEMENTS.has(elementName) &&
                                fileName.toLowerCase() !== 'globals.xml' &&
                                schemaService.globalConsts.has(canonicalName)
                            ) {
                                markers.push({
                                    severity: 'error',
                                    message: `Const '${canonicalName}' is already declared in globals.xml.`,
                                    startLineNumber: prop.lineNumber,
                                    startColumn: prop.startColumn,
                                    endLineNumber: prop.lineNumber,
                                    endColumn: prop.endColumn
                                });
                            }

                            const shouldRegisterDefinition: boolean =
                                nameDefinitionRule !== undefined ||
                                (prop.propName === 'name' &&
                                    isObjectNameProperty &&
                                    NAME_BY_PARENT_RULES.some(
                                        ({ element, parent, attribute = 'name' }) =>
                                            element === elementName &&
                                            attribute === 'name' &&
                                            (parent === null || parent === parentElement)
                                    ));

                            if (shouldRegisterDefinition) {
                                const callbackInfo: DefinitionInfo = {
                                    source: nameDefinitionRule?.source ?? elementName,
                                    line: prop.lineNumber,
                                    startColumn: prop.startColumn,
                                    endColumn: prop.startColumn + prop.propName.length,
                                    parent: parentElement
                                };

                                if (nameDefinitionRule && isEventCallbackElement(nameDefinitionRule.element)) {
                                    // Callback names are C function references and may repeat in one file.
                                    const callbackEntry: GlobalDefinitionInfo = {
                                        ...callbackInfo,
                                        file: normalizedFilePath || '<unknown>'
                                    };
                                    const existingCallbacks: GlobalDefinitionInfo[] =
                                        EVENT_CALLBACK_DEFINITIONS.get(canonicalName) ?? [];
                                    EVENT_CALLBACK_DEFINITIONS.set(canonicalName, [
                                        ...existingCallbacks,
                                        callbackEntry
                                    ]);
                                } else {
                                    // Consts have a dedicated globals-shadowing message below, so skip
                                    // the generic global-reuse check for them to avoid double reporting.
                                    if (
                                        !inGlobalsDocument &&
                                        parentElement !== 'consts' &&
                                        globalReservedNames.has(canonicalName)
                                    ) {
                                        markers.push({
                                            severity: 'error',
                                            message: `Name '${canonicalName}' is already defined globally and cannot be reused here.`,
                                            startLineNumber: prop.lineNumber,
                                            startColumn: prop.startColumn,
                                            endLineNumber: prop.lineNumber,
                                            endColumn: prop.endColumn
                                        });
                                    }

                                    registerDefinition(
                                        canonicalName,
                                        callbackInfo,
                                        isGlobalNameDefinition(elementName, parentElement || null),
                                        [...parentStack].reverse().find((entry: ParentStackEntry) => entry.ifTarget)
                                            ?.ifTarget
                                    );
                                }
                            }
                        }
                    }

                    // Named UI elements (widget/component instances) inside <view> must be unique so
                    // they can be referenced unambiguously (animation targets, subject bindings, ...).
                    if (
                        prop.propName === 'name' &&
                        nameDefinitionRule === undefined &&
                        WIDGET_INSTANCE_ELEMENT_TYPES.has(elementType) &&
                        parentStack.some((entry: ParentStackEntry) => entry.name === 'view')
                    ) {
                        const widgetName: string = prop.propValue.trim();
                        if (widgetName && !widgetName.startsWith('$') && !widgetName.startsWith('#')) {
                            registerDefinition(
                                widgetName,
                                {
                                    source: 'widget',
                                    line: prop.lineNumber,
                                    startColumn: prop.startColumn,
                                    endColumn: prop.startColumn + prop.propName.length,
                                    parent: parentElement
                                },
                                false,
                                parentEntry?.ifTarget
                            );
                        }
                    }

                    // Validate API references
                    const compatibleSubjects: string[] = getCompatibleSubjectSuggestions(
                        schemaService,
                        prop.propName,
                        propType,
                        propsAndArgsToValidate
                    );
                    const validApiNames: string[] = Array.from(
                        new Set<string>([
                            ...compatibleSubjects,
                            ...schemaService.globalApiProps.keys(),
                            ...documentApiPropNames
                        ])
                    );
                    if (
                        prop.propValue.startsWith('$') &&
                        !isTranslationTextProperty &&
                        !validApiNames.includes(prop.propValue.substring(1)) &&
                        prop.propName !== 'symbols'
                    ) {
                        markers.push({
                            severity: 'error',
                            message: `Invalid API or subject reference value '${
                                prop.propValue
                            }', valid options are: ${validApiNames.map((vap) => `$${vap}`).join(', ')}`,
                            ...getPropValueRange(prop)
                        });
                        continue;
                    }
                }

                if (elementName === 'children' && parentElement === 'api') {
                    const allowProp: PropInfo | undefined = propsAndArgsToValidate.find(
                        (prop: PropInfo) => prop.propName === 'allow'
                    );
                    if (!allowProp?.propValue?.trim()) {
                        markers.push({
                            severity: 'error',
                            message: `Element '<children>' requires an 'allow' attribute listing permitted view child tokens.`,
                            ...elementNameRange
                        });
                    } else if (parentStack[0]?.name === 'component' || parentStack[0]?.name === 'widget') {
                        const allowTokens: string[] = allowProp.propValue.trim().split(/\s+/).filter(Boolean);

                        for (const duplicate of getDuplicateChildrenAllowTokenRanges(allowProp)) {
                            markers.push({
                                severity: 'error',
                                message: `Duplicate <children allow> token: '${duplicate.token}'`,
                                startLineNumber: duplicate.lineNumber,
                                startColumn: duplicate.startColumn,
                                endLineNumber: duplicate.lineNumber,
                                endColumn: duplicate.endColumn
                            });
                        }

                        const unknownTokens: string[] = allowTokens.filter((token) => {
                            const resolvedFromSchema = schemaService.resolveApiChildrenAllow(fileBaseName, [token]);
                            if (resolvedFromSchema.length > 0) return false;

                            const rootElementName = parentStack[0]?.name;
                            const isDefiningComponentOrWidget =
                                rootElementName === 'component' || rootElementName === 'widget';
                            if (isDefiningComponentOrWidget && token === fileBaseName) return false;

                            return true;
                        });
                        if (unknownTokens.length) {
                            markers.push({
                                severity: 'error',
                                message: `Unknown <children allow> token(s): ${unknownTokens.join(', ')}`,
                                startLineNumber: allowProp.lineNumber,
                                startColumn: allowProp.startColumn,
                                endLineNumber: allowProp.lineNumber,
                                endColumn: allowProp.endColumn
                            });
                        }
                    }
                }

                if (foundSubjectForValueValidation) foundSubjectForValueValidation = undefined;
            }

            // Check if this element is allowed as a child
            if (parentElement) {
                const parentToUse: string = resolveAllowedChildrenParentName(schemaService, parentEntry, fileBaseName);
                const allowedChildren: string[] = schemaService.getAllowedChildrenForElement(parentToUse);
                if (!isAllowedChildElement(elementName, allowedChildren)) {
                    const message: string =
                        parentElement === 'widget' && elementName === 'animations'
                            ? 'The <animations> tag is currently not supported inside <widget>.'
                            : `Element '${elementName}' is not allowed as child of '${parentToUse}'`;

                    markers.push({
                        severity: 'error',
                        message,
                        ...elementNameRange
                    });
                }

                // Check that this element respects its parent's required child order (e.g.
                // <consts> before <styles> before <view>), which the LVGL runtime parser depends on.
                const parentOrder: string[][] | undefined = schemaService.getElement(parentElement)?.order;
                if (parentEntry && parentOrder) {
                    const orderResult: OrderValidationResult | undefined = validateElementOrder(
                        elementName,
                        parentOrder,
                        parentEntry.maxOrderGroupIndex
                    );
                    if (orderResult) {
                        if (!orderResult.isValid && orderResult.message) {
                            markers.push({
                                severity: 'error',
                                message: orderResult.message,
                                ...elementNameRange
                            });
                        }
                        // Calculate the max order group index, seen so far, for this parent
                        // so that subsequent children can be validated against it.
                        if (orderResult.groupIndex !== undefined) {
                            parentEntry.maxOrderGroupIndex = Math.max(
                                parentEntry.maxOrderGroupIndex ?? -1,
                                orderResult.groupIndex
                            );
                        }
                    }
                }
            }

            // Check if the element must be unique
            if (UNIQUE_ELEMENTS?.[elementName] === false) UNIQUE_ELEMENTS[elementName] = true;
            else if (UNIQUE_ELEMENTS?.[elementName] === true) {
                markers.push({
                    severity: 'error',
                    message: `Element '${elementName}' must be unique and defined only once`,
                    ...elementNameRange
                });
            }

            // Only add to parent stack if it is not a self-closing tag
            if (!fullMatch.endsWith('/>')) {
                const ifTargetMatch: RegExpMatchArray | null = attributes.match(/\bif_target\s*=\s*["']([^"']*)["']/);
                parentStack.push({ name: elementName, extends: elementExtends, ifTarget: ifTargetMatch?.[1] });
            }
        }
    }

    // Check for unclosed elements at the end
    if (parentStack.length > 0) {
        markers.push({
            severity: 'error',
            message: `Unclosed elements: ${parentStack.map((entry) => entry.name).join(', ')}`,
            startLineNumber: model.getLineCount(),
            startColumn: 1,
            endLineNumber: model.getLineCount(),
            endColumn: model.getLineLength(model.getLineCount()) + 1
        });
    }

    // Reset the validation mappers and flags
    Object.keys(UNIQUE_ELEMENTS).forEach((key: string) => (UNIQUE_ELEMENTS[key] = false));
    foundSubjectForValueValidation = undefined;
    hasWidget = false;

    return markers;
};

/** All global definition names registered while validating project XML files. */
export const getKnownGlobalDefinitionNames = (): string[] => [...GLOBAL_NAME_DEFINITIONS.keys()];

/** Screen names registered from <screen> roots while validating project XML files. */
export const getCachedScreenNames = (): string[] =>
    [...GLOBAL_NAME_DEFINITIONS.entries()]
        .filter(([, info]) => info.source === 'screen')
        .map(([name]) => name)
        .sort((a, b) => a.localeCompare(b));

/** Remove screen definitions previously registered from a file (e.g. after rename). */
export const clearGlobalScreenDefinitionsForFile = (filePath: string): void => {
    clearDefinitionsForFile(filePath, (info: GlobalDefinitionInfo): boolean => info.source === 'screen');
};

/** Remove all validation definitions previously registered from a file (e.g. after move/delete). */
export const clearValidationDefinitionsForFile = (filePath: string): void => {
    clearDefinitionsForFile(filePath);
};

/** Event callback names collected from event_cb elements while validating project XML files. */
export const getKnownEventCallbackNames = (): string[] =>
    [...EVENT_CALLBACK_DEFINITIONS.entries()]
        .filter(([, entries]) => entries.length > 0)
        .map(([name]) => name)
        .sort((a, b) => a.localeCompare(b));

/** Clears collected project-level validation state, e.g. after changing projects. */
export const clearKnownProjectValidationState = (): void => {
    GLOBAL_NAME_DEFINITIONS.clear();
    GLOBAL_DUPLICATE_DEFINITIONS.clear();
    EVENT_CALLBACK_DEFINITIONS.clear();
    Object.keys(UNIQUE_ELEMENTS).forEach((key: string) => (UNIQUE_ELEMENTS[key] = false));
    foundSubjectForValueValidation = undefined;
};

/** Clears collected event callback names (used when re-validating the project). */
export const clearKnownEventCallbackNames = (): void => {
    EVENT_CALLBACK_DEFINITIONS.clear();
};
